"use client"

import { useState, useEffect, useRef } from "react"
import {
  Paper,
  Typography,
  Switch,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
} from "@mui/material"
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles"
import SettingsIcon from "@mui/icons-material/Settings"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import HighlightOffIcon from "@mui/icons-material/HighlightOff"
import { alpha } from "@mui/material/styles"
import { PapiroConsole } from "src/utils/papiroConsole"

// Tema personalizado com a cor principal #3F51B4
const theme = createTheme({
  palette: {
    primary: {
      main: "#3F51B4",
      light: alpha("#3F51B4", 0.1),
    },
    secondary: {
      main: "#F50057",
    },
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 500,
    },
    subtitle1: {
      fontWeight: 500,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: "0",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: alpha("#3F51B4", 0.05),
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
          margin: 8,
        },
        switchBase: {
          padding: 1,
          "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
              backgroundColor: "#3F51B4",
              opacity: 1,
              border: 0,
            },
          },
          "&.Mui-focusVisible .MuiSwitch-thumb": {
            color: "#3F51B4",
            border: "6px solid #fff",
          },
        },
        thumb: {
          width: 24,
          height: 24,
        },
        track: {
          borderRadius: 26 / 2,
          border: `1px solid ${alpha("#000", 0.1)}`,
          backgroundColor: alpha("#000", 0.2),
          opacity: 1,
          transition: "background-color 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          fontWeight: 500,
        },
      },
    },
  },
})

// Função auxiliar para garantir que os dados sejam seguros para renderização
const safeRender = (content) => {
  if (content === null || content === undefined) {
    return ""
  }

  if (typeof content === "object") {
    return JSON.stringify(content)
  }

  return content
}

// Exportando a função como default
export default function StatusManager({ onChange, initialStatuses = null }) {
  const [statuses, setStatuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Referência para armazenar o estado inicial dos status
  const initialStatusesRef = useRef(null)
  // Referência para controlar se a notificação inicial já foi enviada
  const initialNotificationSentRef = useRef(false)

  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"))

  // Buscar os status ao carregar o componente
  useEffect(() => {
    try {
      if (initialStatuses) {
        // Validar que initialStatuses é um array
        if (!Array.isArray(initialStatuses)) {
          throw new Error("initialStatuses deve ser um array")
        }

        // Validar a estrutura de cada status
        const validatedStatuses = initialStatuses.map((status) => {
          if (!status || typeof status !== "object") {
            return {
              id: Math.random(),
              name: "Status inválido",
              visibleincalledediting: false,
              visibleincalledlistfilter: false,
              description: "Status com formato inválido",
              color: "#999999",
            }
          }

          return {
            id: status.id || Math.random(),
            name: status.name || "Status sem nome",
            visibleincalledediting: Boolean(status.visibleincalledediting),
            visibleincalledlistfilter: Boolean(status.visibleincalledlistfilter),
            description: status.description || "",
            color: status.color || "#999999",
            configcalledsstatuslistid: status.configcalledsstatuslistid
          }
        })

        PapiroConsole.log("validatedStatuses")
        PapiroConsole.log(validatedStatuses)

        setStatuses(validatedStatuses)
        // Armazenar o estado inicial para comparação posterior
        initialStatusesRef.current = JSON.parse(JSON.stringify(validatedStatuses))
      } else {
        // Se não houver status iniciais, definir um array vazio
        setStatuses([])
        initialStatusesRef.current = []
      }
    } catch (err) {
      console.error("Erro ao processar status iniciais:", err)
      setError("Erro ao processar os status. Por favor, recarregue a página.")
      setStatuses([])
      initialStatusesRef.current = []
    } finally {
      setLoading(false)
    }
  }, [initialStatuses]) // Remover onChange das dependências

  // Notificar o componente pai sobre os status iniciais apenas uma vez após o carregamento
  useEffect(() => {
    if (!loading && initialStatuses && onChange && !initialNotificationSentRef.current) {
      try {
        // Marcar que a notificação inicial foi enviada
        initialNotificationSentRef.current = true

        // Usar um setTimeout para garantir que isso aconteça após a renderização
        const timer = setTimeout(() => {
          onChange({
            allStatuses: statuses,
            changedStatuses: [], // Inicialmente não há status alterados
          })
        }, 0)

        return () => clearTimeout(timer)
      } catch (err) {
        console.error("Erro ao notificar componente pai:", err)
      }
    }
  }, [loading, statuses, onChange, initialStatuses])

  // Função para identificar quais status foram alterados
  const getChangedStatuses = (currentStatuses) => {
    if (!initialStatusesRef.current) return []

    try {
      return currentStatuses.filter((status) => {
        const initialStatus = initialStatusesRef.current.find((s) => s.id === status.id)
        return (
          initialStatus &&
          (initialStatus.visibleincalledediting !== status.visibleincalledediting ||
            initialStatus.visibleincalledlistfilter !== status.visibleincalledlistfilter)
        )
      })
    } catch (err) {
      console.error("Erro ao identificar status alterados:", err)
      return []
    }
  }

  const handleEditVisibilityChange = (id) => {
    try {
      const updatedStatuses = statuses.map((status) =>
        status.id === id ? { ...status, visibleincalledediting: !status.visibleincalledediting } : status,
      )

      setStatuses(updatedStatuses)

      if (onChange) {
        const changedStatuses = getChangedStatuses(updatedStatuses)
        onChange({
          allStatuses: updatedStatuses,
          changedStatuses: changedStatuses,
        })
      }
    } catch (err) {
      console.error("Erro ao alterar visibilidade na edição:", err)
      setError("Erro ao alterar o status. Por favor, tente novamente.")
    }
  }

  const handleFilterVisibilityChange = (id) => {
    try {
      const updatedStatuses = statuses.map((status) =>
        status.id === id ? { ...status, visibleincalledlistfilter: !status.visibleincalledlistfilter } : status,
      )

      setStatuses(updatedStatuses)

      if (onChange) {
        const changedStatuses = getChangedStatuses(updatedStatuses)
        onChange({
          allStatuses: updatedStatuses,
          changedStatuses: changedStatuses,
        })
      }
    } catch (err) {
      console.error("Erro ao alterar visibilidade no filtro:", err)
      setError("Erro ao alterar o status. Por favor, tente novamente.")
    }
  }

  // Renderização para dispositivos móveis
  const renderMobileView = () => {
    if (!Array.isArray(statuses) || statuses.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="text.secondary">Nenhum status disponível.</Typography>
        </Box>
      )
    }

    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        {statuses.map((status) => {
          if (!status || typeof status !== "object" || !status.id) {
            return null
          }

          const isChanged =
            initialStatusesRef.current &&
            initialStatusesRef.current.find((s) => s.id === status.id) &&
            (initialStatusesRef.current.find((s) => s.id === status.id)?.visibleincalledediting !== status.visibleincalledediting ||
              initialStatusesRef.current.find((s) => s.id === status.id)?.visibleincalledlistfilter !==
                status.visibleincalledlistfilter)

          return (
            <Card
              key={status.id}
              variant="outlined"
              sx={{
                borderLeft: `4px solid ${status.color || "#999"}`,
                borderColor: status.visibleincalledediting || status.visibleincalledlistfilter ? status.color || "#999" : "divider",
                backgroundColor:
                  status.visibleincalledediting || status.visibleincalledlistfilter
                    ? alpha(status.color || "#3F51B4", 0.05)
                    : "background.paper",
                transition: "all 0.3s ease",
                position: "relative",
                ...(isChanged && {
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    right: 8,
                    top: 8,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#F50057",
                  },
                }),
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box>
                  <Typography variant="subtitle1">{safeRender(status.name)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", mb: 2 }}>
                    {safeRender(status.description)}
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Visível na edição
                      </Typography>
                      <Switch
                        checked={Boolean(status.visibleincalledediting)}
                        onChange={() => handleEditVisibilityChange(status.id)}
                        color="primary"
                        size="small"
                      />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Visível no filtro
                      </Typography>
                      <Switch
                        checked={Boolean(status.visibleincalledlistfilter)}
                        onChange={() => handleFilterVisibilityChange(status.id)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )
        })}
      </Stack>
    )
  }
  function isValidCssColor(strColor) {
  const s = new Option().style;
  s.color = strColor;
  return s.color !== '';
}

  function getSafeColor(color, fallback = "#3F51B4") {
    if (
      typeof color !== "string" ||
      color.trim() === "" ||
      color.trim().toLowerCase() === "undefined" ||
      !isValidCssColor(color.trim())
    ) {
      return fallback;
    }
    return color.trim();
  }
  // Renderização para desktop
  const renderDesktopView = () => {
    if (!Array.isArray(statuses) || statuses.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="text.secondary">Nenhum status disponível.</Typography>
        </Box>
      )
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 2, boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
        <Table aria-label="tabela de status de chamados">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="center">Visível na Edição</TableCell>
              <TableCell align="center">Visível no Filtro</TableCell>
              <TableCell align="center">Estado Geral</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statuses.map((status) => {
              if (!status || typeof status !== "object" || !status.id) {
                return null
              }

              const isChanged =
                initialStatusesRef.current &&
                initialStatusesRef.current.find((s) => s.id === status.id) &&
                (initialStatusesRef.current.find((s) => s.id === status.id)?.visibleincalledediting !==
                  status.visibleincalledediting ||
                  initialStatusesRef.current.find((s) => s.id === status.id)?.visibleincalledlistfilter !==
                    status.visibleincalledlistfilter)

              const isAnyVisible = status.visibleincalledediting || status.visibleincalledlistfilter

              return (
                <TableRow
                  key={status.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: isAnyVisible ? alpha(getSafeColor(status.color), 0.05) : "transparent",
                    ...(isChanged && {
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        right: 8,
                        top: 8,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#F50057",
                      },
                    }),
                    transition: "background-color 0.3s ease",
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Chip
                        label={safeRender(status.name)}
                        size="small"
                        sx={{
                          backgroundColor: status.color || "#999",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "100px",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {safeRender(status.description)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={Boolean(status.visibleincalledediting)}
                      onChange={() => handleEditVisibilityChange(status.id)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={Boolean(status.visibleincalledlistfilter)}
                      onChange={() => handleFilterVisibilityChange(status.id)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {isAnyVisible ? (
                      <Chip
                        icon={<CheckCircleOutlineIcon />}
                        label={
                          status.visibleincalledediting && status.visibleincalledlistfilter
                            ? "Ambos"
                            : status.visibleincalledediting
                              ? "Edição"
                              : "Filtro"
                        }
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        icon={<HighlightOffIcon />}
                        label="Oculto"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  // Renderizar mensagem de erro se houver
  if (error) {
    return (
      <Paper elevation={0} sx={{ p: 3, my: 2, backgroundColor: "#FFEBEE", borderLeft: "4px solid #F44336" }}>
        <Typography variant="subtitle1" color="error">
          Erro
        </Typography>
        <Typography variant="body2">{error}</Typography>
      </Paper>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Paper elevation={0} sx={{ maxWidth: 900, mx: "auto", my: 2, overflow: "hidden" }}>
        <Box
          sx={{
            p: 3,
            backgroundColor: "primary.light",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center">
            <SettingsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h5" color="primary">
              Configuração de Status de Chamados
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Selecione os status que deseja exibir na tela de chamados. Os status que estiverem com visibilidade desmarcada não seram exibidos
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>{isMobile ? renderMobileView() : renderDesktopView()}</>
          )}
        </Box>
      </Paper>
    </ThemeProvider>
  )
}
